import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Wallet as WalletIcon, ShieldCheck, Coins, TrendingUp, Loader2, Copy, ArrowRightLeft, Leaf } from "lucide-react";
import { toast } from "sonner";
import { RetrofitCertificates } from "@/components/wallet/RetrofitCertificates";

export default function Wallet() {
  const [w, setW] = useState(null);
  useEffect(() => { api.get("/wallet").then((r) => setW(r.data)).catch(() => setW(false)); }, []);

  if (!w) return <div className="min-h-screen grid place-items-center bg-[#050A15]"><Loader2 className="h-7 w-7 animate-spin text-[#00FFFF]" /></div>;

  const copy = () => { navigator.clipboard.writeText(w.address); toast.success("Address copied"); };
  const short = (h) => h ? `${h.slice(0, 12)}…${h.slice(-8)}` : "";

  return (
    <div className="min-h-screen bg-[#050A15]">
      <Header />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        <h1 className="font-display font-bold text-3xl flex items-center gap-2"><WalletIcon className="h-7 w-7 text-[#00FF7F]" /> Custodial Wallet</h1>

        <div className="qs-grad-border p-6 mt-6" data-testid="wallet-address">
          <p className="text-xs uppercase tracking-widest text-slate-500">Wallet address · {w.network}</p>
          <div className="flex items-center gap-3 mt-2">
            <code className="font-mono text-[#00FFFF] text-sm break-all">{w.address}</code>
            <button onClick={copy} className="text-slate-500 hover:text-[#00FFFF]" data-testid="copy-address"><Copy className="h-4 w-4" /></button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <Stat icon={Coins} c="#00FF7F" label="Carbon Credits" value={`${w.carbon_credits} ZCC`} />
          <Stat icon={Leaf} c="#F59E0B" label="ZCC certificates" value={w.retrofit_verified || 0} testId="wallet-stat-zcc-certs" />
          <Stat icon={ShieldCheck} c="#00FFFF" label="Certificates" value={w.certificate_count} />
          <Stat icon={TrendingUp} c="#8A2BE2" label="Investments" value={w.investment_count} />
        </div>

        <div className="mt-8"><RetrofitCertificates rows={w.retrofit_credits || []} verified={w.retrofit_verified || 0} provisional={w.retrofit_provisional || 0} /></div>

        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <div className="qs-panel p-6">
            <h3 className="font-display font-semibold mb-4">Certificates</h3>
            {w.certificates.length === 0 ? <Empty text="No certificates yet." /> : (
              <div className="space-y-3" data-testid="cert-list">
                {w.certificates.map((c) => (
                  <div key={c.tx_hash} className="border border-white/10 p-4">
                    <div className="flex justify-between items-start">
                      <span className="text-sm font-medium">{c.cert_type}</span>
                      <span className="text-[10px] font-mono px-2 py-0.5 border border-[#00FFFF]/30 text-[#00FFFF]">{c.standard}</span>
                    </div>
                    <div className="font-mono text-xs text-slate-500 mt-2">#{c.token_id} · {c.project}</div>
                    <div className="font-mono text-xs text-slate-600 mt-0.5 break-all">{short(c.tx_hash)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="qs-panel p-6">
            <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><ArrowRightLeft className="h-4 w-4 text-slate-400" /> Transaction History</h3>
            {w.transactions.length === 0 ? <Empty text="No transactions yet." /> : (
              <div className="space-y-2" data-testid="tx-list">
                {w.transactions.map((t) => (
                  <div key={t.tx_hash} className="flex justify-between items-center border-b border-white/5 py-2.5 text-sm">
                    <div>
                      <div className="text-slate-200">{t.type}</div>
                      <div className="text-xs text-slate-500">{t.detail}</div>
                    </div>
                    <div className="text-right font-mono text-xs text-slate-600">
                      <div className="text-[#00FF7F]">{short(t.tx_hash)}</div>
                      <div>{t.date ? new Date(t.date).toLocaleDateString() : ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, c, testId }) => (
  <div className="qs-panel p-5" data-testid={testId}>
    <Icon className="h-6 w-6" style={{ color: c }} />
    <div className="font-mono text-2xl font-bold mt-3" style={{ color: c }}>{value}</div>
    <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{label}</div>
  </div>
);

const Empty = ({ text }) => <p className="text-sm text-slate-500 py-6 text-center">{text}</p>;
